// AWARENESS-MVP (docs/design/2026-11-01-companyos-awareness-and-ideas-board.md §3): the seat
// drawer's "Boards you use" line for a DE seat: the boards a person told this seat's DE to use,
// each with what it is for (Ideas: file ideas here; your pieces; dates). The DE's platform block
// and wake prompt name them by id. Owners and admins edit (people only); it is read-only for
// everyone else, and hidden from who may not read it (the route is owners' and admins').
import type { OperationOutput } from "@companyos/sdk";
import { Button, InfoTip, toast } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, XIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { FormError } from "../structure/parts";

type View = OperationOutput<"seats.boards">;
type Use = View["boards"][number];
type Purpose = Use["purpose"];

export const PURPOSE_LABELS: Record<Purpose, string> = {
  ideas: "file ideas here",
  pieces: "your pieces",
  dates: "dates",
  other: "other",
};

const boardsKey = (co: string, seatId: string) => ["seat-boards", co, seatId] as const;

export function SeatBoardsLine({
  api,
  company,
  seatId,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
}) {
  const qc = useQueryClient();
  const co = company.id;
  const q = useQuery({
    queryKey: boardsKey(co, seatId),
    queryFn: () => api.client.seats.boards({ params: { co, id: seatId } }),
    retry: false,
  });
  const [adding, setAdding] = useState(false);
  const all = useQuery({
    queryKey: ["seat-boards-options", co],
    queryFn: () => api.client.boards.list({ params: { co }, query: { limit: 200 } }),
    enabled: adding,
  });
  const save = useMutation({
    mutationFn: (boards: Use[]) =>
      api.client.seats.boardsUpdate({
        params: { co, id: seatId },
        body: {
          boards: boards.map((b) => ({ board_id: b.board_id, purpose: b.purpose, note: b.note })),
        },
      }),
    onSuccess: (r) => {
      qc.setQueryData(boardsKey(co, seatId), r);
      setAdding(false);
      toast.add({ title: "Boards saved", type: "success" });
    },
  });
  if (q.isPending || q.isError || !q.data) return null;
  const uses = q.data.boards;
  const editable = q.data.editable;
  const taken = new Set(uses.map((u) => u.board_id));
  const hasIdeas = uses.some((u) => u.purpose === "ideas");
  const options = (all.data?.data ?? []).filter((b) => !taken.has(b.id) && !b.archived_at);

  return (
    <div className="mt-1.5 flex flex-col gap-1" data-slot="seat-boards">
      <span className="flex flex-wrap items-center gap-1 text-xs">
        <span className="text-faint">Boards you use</span>
        {uses.length === 0 ? (
          <span className="text-faint" data-slot="seat-boards-empty">
            None
          </span>
        ) : null}
        {uses.map((u) => (
          <span
            key={u.board_id}
            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
            data-slot="seat-board-chip"
            data-purpose={u.purpose}
          >
            <span>{u.board_name}</span>
            <span className="text-faint">({u.note ?? PURPOSE_LABELS[u.purpose]})</span>
            {editable ? (
              <button
                type="button"
                aria-label={`Stop using ${u.board_name}`}
                disabled={save.isPending}
                onClick={() => save.mutate(uses.filter((x) => x.board_id !== u.board_id))}
              >
                <XIcon className="size-3" aria-hidden="true" />
              </button>
            ) : null}
          </span>
        ))}
        {editable ? (
          <Button
            size="xs"
            variant="ghost"
            data-slot="seat-boards-add"
            onClick={() => setAdding((v) => !v)}
          >
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            Add board
          </Button>
        ) : null}
        <InfoTip label="About Boards you use">
          The boards this seat&apos;s DE is told to use, by id. An Ideas board is where it files
          side ideas for you to approve or reject.
        </InfoTip>
      </span>
      {adding ? (
        <span className="flex flex-wrap items-center gap-1" data-slot="seat-boards-picker">
          {all.isPending ? <span className="text-faint text-xs">Loading boards…</span> : null}
          {options.map((b) => {
            // A board made from the Ideas template (its statuses) is the Ideas board.
            const keys = new Set(b.status_set.map((s) => s.key));
            const purpose: Purpose =
              !hasIdeas && keys.has("proposed") && keys.has("used") ? "ideas" : "pieces";
            return (
              <Button
                key={b.id}
                size="xs"
                variant="secondary"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate([
                    ...uses,
                    { board_id: b.id, board_name: b.name, purpose, note: null },
                  ])
                }
              >
                {b.name} ({PURPOSE_LABELS[purpose]})
              </Button>
            );
          })}
          {!all.isPending && options.length === 0 ? (
            <span className="text-faint text-xs">No other boards</span>
          ) : null}
        </span>
      ) : null}
      <FormError what="Saving the boards" error={save.error} />
    </div>
  );
}
